import React from 'react'
import { MenuItem, TextField } from '@mui/material'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import PushPanel from './PushPanel'
import { formFieldSx, selectProps } from './pushStyles'
import { BODY_MAX, TITLE_MAX } from './pushUtils'

export interface ComposeValues {
    title: string
    body: string
    destination: string
}

export type ComposeErrors = Partial<Record<keyof ComposeValues, string>>

export const emptyCompose = (): ComposeValues => ({ title: '', body: '', destination: '' })

/** Plain validation (mirrors the backend limits; the server remains authoritative). */
export const validateCompose = (v: ComposeValues): ComposeErrors => {
    const errors: ComposeErrors = {}
    const title = v.title.trim()
    const body = v.body.trim()
    if (!title) errors.title = 'Title is required.'
    else if (title.length > TITLE_MAX) errors.title = `Title must be at most ${TITLE_MAX} characters.`
    if (!body) errors.body = 'Message is required.'
    else if (body.length > BODY_MAX) errors.body = `Message must be at most ${BODY_MAX} characters.`
    return errors
}

interface Props {
    values: ComposeValues
    onChange: (v: ComposeValues) => void
    /** Client-side errors, only shown once the admin tried to continue. */
    errors?: ComposeErrors
    /** Errors returned by the server (422). */
    serverErrors?: Record<string, string>
    destinations: { key: string; label: string }[]
    destinationsLoading?: boolean
}

const ComposeForm: React.FC<Props> = ({ values, onChange, errors = {}, serverErrors = {}, destinations, destinationsLoading }) => {
    const set = (k: keyof ComposeValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChange({ ...values, [k]: e.target.value })
    const titleErr = errors.title || serverErrors.title
    const bodyErr = errors.body || serverErrors.body

    return (
        <PushPanel title="Message" icon={<EditNoteOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}>
            <TextField
                fullWidth
                label="Title"
                value={values.title}
                onChange={set('title')}
                error={Boolean(titleErr)}
                helperText={titleErr || `${values.title.length}/${TITLE_MAX}`}
                sx={{ ...formFieldSx, mb: 2 }}
            />
            <TextField
                fullWidth
                multiline
                minRows={3}
                label="Message"
                value={values.body}
                onChange={set('body')}
                error={Boolean(bodyErr)}
                helperText={bodyErr || `${values.body.length}/${BODY_MAX}`}
                sx={{ ...formFieldSx, mb: 2 }}
            />
            <TextField
                select
                fullWidth
                label="Opens (optional destination)"
                value={values.destination}
                onChange={set('destination')}
                disabled={destinationsLoading}
                error={Boolean(serverErrors.destination)}
                helperText={serverErrors.destination ?? 'Only supported in-app destinations are listed. Leave empty to open the app.'}
                sx={formFieldSx}
                slotProps={{ select: selectProps }}
            >
                <MenuItem value=""><em>Default (open app home)</em></MenuItem>
                {destinations.map((d) => (
                    <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>
                ))}
            </TextField>
        </PushPanel>
    )
}

export default ComposeForm
